import { useEffect, useMemo, useState } from "react";
import { ArrowCounterClockwise, CheckSquare, Square } from "@phosphor-icons/react";

function nodeText(n) {
  if (!n) return "";
  if (n.type === "text") return n.value;
  return (n.children || []).map(nodeText).join("");
}

// Interactive GFM task list: ticks saved per message in localStorage, minutes progress, reset.
export default function ChecklistWidget({ node, storageKey }) {
  const items = useMemo(
    () =>
      (node.children || [])
        .filter((c) => c.type === "element" && c.tagName === "li")
        .map((li, i) => {
          const text = nodeText(li).trim();
          const m = /(\d+)\s*min/i.exec(text);
          return { id: i, text, minutes: m ? parseInt(m[1], 10) : 0 };
        }),
    [node]
  );
  const key = `hub-checklist:${storageKey || "anon"}`;
  const [done, setDone] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(key) || "[]")); } catch { return new Set(); }
  });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify([...done])); } catch { /* ignore */ } }, [done, key]);

  const total = items.reduce((s, it) => s + it.minutes, 0);
  const doneMin = items.filter((it) => done.has(it.id)).reduce((s, it) => s + it.minutes, 0);
  const pct = total ? Math.round((doneMin / total) * 100) : Math.round((done.size / Math.max(items.length, 1)) * 100);
  const toggle = (id) => setDone((d) => { const n = new Set(d); n.has(id) ? n.delete(id) : n.add(id); return n; });

  return (
    <div className="hub-checklist" data-testid="hub-checklist">
      <div className="hub-checklist-head">
        <span className="hub-checklist-progress" data-testid="hub-checklist-progress">
          {total ? `${doneMin}/${total} minutes` : `${done.size}/${items.length} done`}
        </span>
        <button type="button" className="hub-checklist-reset" onClick={() => setDone(new Set())} data-testid="hub-checklist-reset">
          <ArrowCounterClockwise size={13} weight="bold" /> Reset week
        </button>
      </div>
      <div className="hub-checklist-bar"><i style={{ width: `${pct}%` }} /></div>
      <ul className="hub-checklist-items">
        {items.map((it) => (
          <li key={it.id}>
            <button type="button" className={`hub-checklist-item${done.has(it.id) ? " is-done" : ""}`} onClick={() => toggle(it.id)} data-testid={`hub-checklist-item-${it.id}`} aria-pressed={done.has(it.id)}>
              {done.has(it.id) ? <CheckSquare size={18} weight="fill" /> : <Square size={18} />}
              <span>{it.text}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
